"use client";
import { LiveKitRoom } from "@livekit/components-react";
import { FC, useEffect, useRef, useState } from "react";
import { useCreateChatContextMutation } from "../../apis/chat.api";
import { useVoiceChat } from "../../hooks/use-voice-chat";
import { WidgetScene } from "../types/widget-scenes";
import { iChoiceProps } from "./choice";
import LiveKitRoomContent from "./livekit-room-content";

const ChoiceCall: FC<iChoiceProps> = ({ setWidgetStage, agentId, config }) => {
  const [createChatContext] = useCreateChatContextMutation();
  const [chatId, setChatId] = useState<string>("");
  const [callDuration, setCallDuration] = useState("00:00");
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolumeState] = useState(1.0);
  const chatContextCreated = useRef(false);
  const { voiceToken, initializeVoiceChat } = useVoiceChat({
    chatId,
  });

  useEffect(() => {
    if (agentId && !chatId && !chatContextCreated.current) {
      chatContextCreated.current = true;
      const storedChatId = localStorage.getItem(`chatId_${agentId}`);

      if (storedChatId) {
        setChatId(storedChatId);
        return;
      }

      createChatContext({ agentId })
        .unwrap()
        .then((response) => {
          console.debug({ response });
          setChatId(response.chatId);
          localStorage.setItem(`chatId_${agentId}`, response.chatId);
        })
        .catch((error) => {
          console.error("Failed to create chat context:", error);
        });
    }
  }, [agentId, chatId, createChatContext]);

  useEffect(() => {
    if (chatId) {
      initializeVoiceChat();
    }
  }, [chatId, initializeVoiceChat]);

  const onClose = () => {
    setWidgetStage(WidgetScene.CHOICE);
  };

  const onChatSelection = () => {
    setWidgetStage(WidgetScene.CHAT);
  };

  const onEndCall = () => {
    setWidgetStage(WidgetScene.CHOICE);
  };

  const handleMicrophoneToggle = async () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolumeState(newVolume);
  };

  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      setCallDuration(
        `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0"
        )}`
      );
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  if (!config) return null;

  return (
    <LiveKitRoom
      serverUrl={
        process.env.NEXT_PUBLIC_LIVEKIT_URL ||
        "wss://outsella-demo-g3d9d836.livekit.cloud"
      }
      token={voiceToken || ""}
      connect={!!voiceToken}
      audio={true}
      options={{
        publishDefaults: { stopMicTrackOnMute: true },
      }}
      onError={(e) => {
        console.error("LiveKit room error:", e);
      }}
      onDisconnected={() => {
        console.log("LiveKit room disconnected");
      }}
      onConnected={() => {
        console.log("LiveKit room connected");
      }}
    >
      <LiveKitRoomContent
        isMuted={isMuted}
        volume={volume}
        callDuration={callDuration}
        config={config}
        onClose={onClose}
        onChatSelection={onChatSelection}
        onEndCall={onEndCall}
        handleMicrophoneToggle={handleMicrophoneToggle}
        handleVolumeChange={handleVolumeChange}
      />
    </LiveKitRoom>
  );
};

export default ChoiceCall;
